import * as events from './event-bus';
import * as env from './env';
import * as configcat from 'configcat-js';
import * as store from './store';
import CryptoJS from 'crypto-js';
import { FLAG_CACHE_EXPIRY_MINUTES } from "./constants.js";

let user = null;
let apiEndpoint = null;
let allFlags = null;
let isFeatureFlagUp = true;
let configCatClient = null;

const envEndpointMap = {
	dev: 'https://featureflag-dev-api.ss.frontlineeducation.com',
	qa: 'https://featureflag-qa-api.ss.frontlineeducation.com',
	stage: 'https://featureflag-stage-api.ss.frontlineeducation.com',
	prod: 'https://featureflag-api.frontlineeducation.com'
};

const envKeyMap = {
	dev: 'V_7XCIkq50CKzPmiM0YU_A/zGpBEFoGtUy_xXKOOMVQYg',
	qa: 'V_7XCIkq50CKzPmiM0YU_A/5xT5gM6S3kuTzzm8KggIrw',
	stage: 'V_7XCIkq50CKzPmiM0YU_A/6UvuIQghx0Se-FLvdJEQyw',
	prod: 'V_7XCIkq50CKzPmiM0YU_A/VSadpU-w2kSpvTsADlOK1Q'
};

// this function generates a hmac token and timestamp
function generateHmacToken(secretKey, data) {
    const timestamp = Math.floor(Date.now() / 1000);
    const payload = `${data}:${timestamp}`;
    const hmac = CryptoJS.HmacSHA256(payload, secretKey).toString(CryptoJS.enc.Hex);
    return { hmac, timestamp };
}

const apiRequest = (url, body) => {
	const secretKey = process.env.HMAC_SECRET_KEY;
    const { hmac, timestamp } = generateHmacToken(secretKey, body);

    const fetchPromise = fetch(url, {
        method: 'POST',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': `${hmac}`
        },
        body: JSON.stringify({ data: body, timestamp })
    }).then((res) => res.json());

    const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timed out')), 2000)
    );

    return Promise.race([fetchPromise, timeoutPromise])
        .catch(() => {
            if(body.defaultValue){
                return {
                    value: body.defaultValue
                };
            }else{
                return [];
            }
        });
};


export function setupPassportFeatureFlagsCC(context) {
	const { userInfo } = context;
	// bypass feature flag lookup if domain context is unavailable (used below)
	// context can be unavailable within My Frontline
	if (!userInfo.domain) {
		return;
	}

	const envName = env.environmentFromPassportDomain(window.location.host);
	apiEndpoint = envEndpointMap[envName];

	// Configcat setup based on https://www.configcat.com/docs/sdk-reference/js/
	user = {
		'identifier': userInfo.person.id,
		'custom' : {
			'orgId': userInfo.orgInfo?.orgId ? userInfo.orgInfo?.orgId.toString() : userInfo.domain.id
		}
	};

	apiRequest(`${apiEndpoint}/api/v1/featureFlag/getAll`, {
		user
	}).then((response) => {
		allFlags = response;
		events.emit('feature-flags-ready-cc', { checkState, checkStateByOrg, forceRefresh, getValue });
	});
}


export function setupFeatureFlagsCC(idmData) {
    const idm = store.get('idm');
    if (idm === null) { return; }
    const envName = env.environmentFromIdmGatewayUrl(idm.idGatewayUrl);
    apiEndpoint = envEndpointMap[envName];
    const envCCKey = envKeyMap[envName];
    const orgId = idmData.userContext.OrganizationId;

    // configcat setup based on https://www.configcat.com/docs/sdk-reference/js/
    user = {
        'identifier': idm.flId, //TODO- replace employeeId with personId
        'custom': {
            'orgId': orgId
        }
    };

	apiRequest(`${apiEndpoint}/api/v1/featureFlag/getAll`, {
		user,
	}).then((response) => {
			allFlags = response;
			isFeatureFlagUp = false;
			if (allFlags.length) {
				isFeatureFlagUp = true;

				events.emit("feature-flags-ready-cc", {
					checkState,
					checkStateByOrg,
					forceRefresh,
					getValue,
				});
			}
	})
	.catch(() => {
		isFeatureFlagUp = false;
	})
	.finally(() => {
		if (!isFeatureFlagUp) {
			fallbackToConfigCat(envCCKey, orgId);
		}
	});
}

function setUpConfigCat(orgId) {
	configCatClient
		.getAllValueDetailsAsync(user)
		.then((response) => {
			allFlags = response;

			if (allFlags) {
				saveFlagsInSession(allFlags, orgId);
			  }

			  events.emit('feature-flags-ready-cc', {
				checkState,
				checkStateByOrg,
				forceRefresh,
				getValue,
			});
	});
}

function saveFlagsInSession(flags, orgId) {
	const timeoutDuration = FLAG_CACHE_EXPIRY_MINUTES * 60 * 1000; 
	const expirationTime = Date.now() + timeoutDuration;
  
	const featureFlagData = {
	  data: flags,
	  expires: expirationTime,
	  organizationId: orgId 
	};
  
	sessionStorage.setItem("featureFlags", JSON.stringify(featureFlagData));
	
  }
 
function fallbackToConfigCat(envCCKey, orgId) {
    const { valid, data } = getValidFlagsFromSession(orgId, true);

    if (valid && data) {
        allFlags = data;
        events.emit("feature-flags-ready-cc", {
            checkState,
            checkStateByOrg,
            forceRefresh,
            getValue,
        });
    } else {
    	configCatClient = configcat.getClient(envCCKey, configcat.PollingMode.LazyLoad);
        setUpConfigCat(orgId);
    }
}


function getValidFlagsFromSession(orgId = null, checkOrgId = false) {
    const cachedFlags = sessionStorage.getItem("featureFlags");
    const now = Date.now();
	let result = { valid: false, data: null }

    if (cachedFlags) {
        const parsedFlags = JSON.parse(cachedFlags);
        const { data, expires, organizationId } = parsedFlags;
        if (
            data &&
            expires &&
            now < expires &&
            (!checkOrgId || organizationId === orgId) 
        ) {
            result = { valid: true, data };
        } else {
            sessionStorage.removeItem("featureFlags");
        }
    }
	
    return result;
}
  
/**
 * @param {*} featureKey - flag to check (key)
 * @param {*} featureValue - Optional, for comparing multivariate values
 * If key doesn't exist, configCatClient will return a promise that resolves to false as value of featureKey
 * @returns If key does exist, will return a promise that resolves to whether it matches featureValue
 */
export const checkState = (featureKey, featureValue = true) => {
	const defaultValue = false;
	const flag = allFlags.find(o => o.key === featureKey);
	if(flag && flag.value){
		return Promise.resolve(flag.value === featureValue);
	}else{
		return Promise.resolve(defaultValue === featureValue);
	}
};

/**
 * Helper function to fetch feature flag value from ConfigCat
 * @param {*} featureKey - flag to check (key)
 * @param {*} defaultValue - default value if the flag is not found
 * @param {*} customUser - user object for ConfigCat
 * @param {*} featureValue - value to compare against
 * @returns Promise resolving to whether the feature flag matches the featureValue
 */
function fetchFlagFromConfigCat(
    featureKey,
    defaultValue,
    customUser,
    featureValue 
) {
    return configCatClient
        .getValueDetailsAsync(featureKey, defaultValue, customUser)
        .then((configCatResponse) => {
            return compareFeatureFlag(customUser, configCatResponse) === featureValue;
        });
}

/**
 * Compare feature flags
 * @param {Object} receivedFeatureFlagValue - The original feature flag object
 * @param {Object} configCatFeatureFlag - The response from ConfigCat
 * @returns {boolean}
 */
function compareFeatureFlag(receivedFeatureFlagValue, configCatFeatureFlag) {
    const { productId = '' } = receivedFeatureFlagValue?.custom || {};
    if (!productId) {
        return configCatFeatureFlag.value;
    }
    const productIdArr = (configCatFeatureFlag.value || '').split(',');
    return productIdArr.some(
        (id) => id.trim().toLowerCase() === productId.toLowerCase()
    );
}

/**
 * @param {*} featureKey - flag to check (key)
 * @param {*} featureValue - Optional, for comparing multivariate values
 * If key doesn't exist, configCatClient will return a promise that resolves to false as value of featureKey
 * @param {*} customUser - an object in the format of {'custom': { 'orgId': <org_id> }}.
 * Using this parameter, status of flag for a specific org is fetched.
 * If not given, default user object will be used which is defined above
 * @returns If key does exist, will return a promise that resolves to whether it matches featureValue
 */
export const checkStateByOrg = (
	featureKey,
	featureValue = true,
	customUser = user,
	defaultValue = false
  ) => {
  
	if (isFeatureFlagUp) {
	  return apiRequest(`${apiEndpoint}/api/v1/featureFlag`, {
		key: featureKey,
		defaultValue,
		user: customUser,
	  }).then((response) => {
		return response.value === featureValue;
	  });
	} else {
		const sessionResult = getFlagFromSession(
			featureKey,
			featureValue,
			defaultValue,
			customUser
		  );

		  if (sessionResult !== null) {
			return sessionResult;
		  }

		  return fetchFlagFromConfigCat(
			featureKey,
			defaultValue,
			customUser,
			featureValue
		  );
	}
  
  };

  function getFlagFromSession(
	featureKey,
	featureValue,
	defaultValue,
	customUser
  ) {
	const { valid, data } = getValidFlagsFromSession();
  
	if (valid && data) {
	  const flag = data.find((o) => o.key === featureKey);
	  if (flag && flag.value) {
		return Promise.resolve(compareFeatureFlag(customUser, flag)  === featureValue);
	  } else {
		return Promise.resolve(defaultValue === featureValue);
	  }
	}
  
	return null; 
  }
  

/**
 * This method forces the configcat client to update the config.json which contains the updated flag value.
 * This method can be used by applications which update the configcat features flags from UI and fetches the updated value without reloading the page.
 * It has been decided to not allow applications to do forceRefresh and hence empty method body
 */
export const forceRefresh = () => {};

/**
 * @param {*} featureKey - flag to check (key)
 * If key doesn't exist, configCatClient will return a promise that resolves to null as value of featureKey
 * @returns If key does exist, will return the value
 */
export const getValue = (featureKey) => {
	const defaultValue = null;
	const flag = allFlags.find(o => o.key === featureKey);
	if(flag && flag.value){
		return Promise.resolve(flag.value);
	}else{
		return Promise.resolve(defaultValue);
	}
};
